import {
  MutablePriceMap,
  PriceMap,
  ProductPriceNoData,
  ProductPriceUnavailable,
  ProductPricingContextShape,
} from '../productPricing/ProductPricingContext'
import React from 'react'
import { isProductApiAvailable, searchForProducts } from '../productDataApi/productDataApi'
import { logDebug } from '../debug/debugLogging'
import { PriceTypeRetail } from '../list/PriceTypeLabel'
import { noOpPackQuantityProvider } from '../bom/NoOpPackQuantityProvider'

/**
 * React hook that fetches and stores Clipsal retail pricing on demand.
 * Can be used to provide retail pricing through a generic ProductPricingContext.
 */
export function useRetailProductPriceStore(): ProductPricingContextShape {
  const [retailPriceMap, setRetailPriceMap] = React.useState<PriceMap>({ })
  const [loadingCount, setLoadingCount] = React.useState(0)

  const requestPricing = React.useCallback((bomItemNos: string[]) => {
    const itemNosToFetch = bomItemNos.filter(itemNo => retailPriceMap[itemNo] === ProductPriceNoData)

    if (itemNosToFetch.length === 0) {
      logDebug('useProductPrices', 'pricing requested for', bomItemNos, ' - nothing to fetch')
      return
    }

    const mergeRetailPricing = (newPricing: PriceMap) => {
      setRetailPriceMap(map => ({ ...map, ...newPricing }))
    }

    setLoadingCount(it => it + 1)

    searchForProducts(itemNosToFetch)
      .then(items => {
        const newRetailCache: MutablePriceMap = {}

        logDebug('useProductPrices', 'pricing requested for', bomItemNos, 'fetched for', itemNosToFetch, 'got', items.map(item => item.itemNo))

        items.forEach(it => {
          newRetailCache[it.itemNo] = it.retailPrice
        })

        itemNosToFetch.forEach(itemNo => {
          if (newRetailCache[itemNo] === ProductPriceNoData) {
            logDebug('useProductPrices', 'failed to get retail price', itemNo)
            newRetailCache[itemNo] = ProductPriceUnavailable
          }
        })

        mergeRetailPricing(newRetailCache)
      })
      .finally(() => {
        setLoadingCount(it => it - 1)
      })
  }, [retailPriceMap])

  return {
    priceMap: retailPriceMap,
    requestPricing,
    isFetching: loadingCount !== 0,
    priceTypeLabel: PriceTypeRetail,
    getPackQuantity: noOpPackQuantityProvider,
    isEnabled: isProductApiAvailable(),
  }
}